import type { ViewNode } from "@e308/ux";
import type { QueueKind } from "../manufacturing-types.js";
import type { ConflictIntent } from "../types.js";
import type { AppIntent, QueueDraft } from "./app-source.js";

export type AppNode = ViewNode<AppIntent, string>;

export function range(
  id: string,
  label: string,
  value: number,
  intent: (value: number) => ConflictIntent,
): AppNode {
  return {
    kind: "range-input",
    id,
    label,
    value,
    min: 0,
    max: 100,
    step: 5,
    showTicks: true,
    intent: (next) => gameIntent(id, intent(next)),
  };
}

export function gameAction(id: string, label: string, intent: ConflictIntent): AppNode {
  return appAction(id, label, gameIntent(id, intent));
}

export function appAction(id: string, label: string, intent: AppIntent): AppNode {
  return { kind: "action", id, action: { id, label, enabled: true, intent, blockers: [] } };
}

export function gameIntent(sourceId: string, intent: ConflictIntent): AppIntent {
  return { kind: "game-order", sourceId, intent };
}

export function draftIntent(queue: QueueKind, change: Partial<QueueDraft>): AppIntent {
  return { kind: "set-queue-draft", queue, change };
}

export function move<T>(values: readonly T[], from: number, to: number): T[] {
  const result = [...values];
  const [value] = result.splice(from, 1);
  if (value !== undefined) result.splice(to, 0, value);
  return result;
}

export function heading(id: string, value: string, level: 2 | 3 = 2): AppNode {
  return { kind: "heading", id, level, text: value };
}

export function text(id: string, value: string): AppNode {
  return { kind: "description", id, content: [{ kind: "text", value }] };
}

export function textInput(
  id: string,
  label: string,
  value: string,
  intent: (value: string) => AppIntent,
): AppNode {
  return { kind: "text-input", id, label, value, intent };
}

export function formatGameClock(milliseconds: number): string {
  const seconds = Math.floor(milliseconds / 1_000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export function list(id: string, values: readonly string[]): AppNode {
  return {
    kind: "stack",
    id,
    children: values.map((value, index) => text(`${id}-${index}`, value)),
  };
}

export function name(id: string): string {
  return id
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split("-")
    .map((part) => `${part[0]?.toUpperCase() ?? ""}${part.slice(1)}`)
    .join(" ");
}
